import { useState, useEffect } from "react";
import {
  Loader2,
  FileSignature,
  ExternalLink,
  CheckCircle,
  File,
  Download,
  PenTool,
  FileText,
  Send,
} from "lucide-react";
import { colors, fonts, DOCUMENT_STATUSES } from "../../styles/theme";
import {
  sendNda,
  fetchContactNdas,
  fetchDocuments,
  sendDocument,
  sendDocumentForSignature,
  fmtDate,
} from "../../services/api";

export default function NdaSection({ contact }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchContactNdas(contact.id, contact.email).then((d) => d.ndas || []),
      contact.email
        ? fetchDocuments({ contactEmail: contact.email, limit: 50 }).then((d) => d.documents || [])
        : Promise.resolve([]),
    ])
      .then(([ndas, files]) => {
        const map = new Map();
        [...ndas, ...files].forEach((d) => map.set(d.id, d));
        const merged = [...map.values()].sort(
          (a, b) => new Date(b.updatedAt || b.sentAt || 0) - new Date(a.updatedAt || a.sentAt || 0),
        );
        setDocs(merged);
      })
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [contact.id, contact.email]);

  const handleSendNda = async () => {
    if (sending) return;
    setSending(true);
    setMessage(null);
    try {
      const result = await sendNda({
        contactId: contact.id,
        contactEmail: contact.email,
        contactName: `${contact.firstName || ""} ${contact.lastName || ""}`.trim(),
        companyName: contact.company || "",
      });
      setMessage({ type: "success", text: "NDA sent for signature" });
      if (result.document) setDocs((prev) => [result.document, ...prev]);
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to send NDA" });
    } finally {
      setSending(false);
    }
  };

  const handleSendFile = async (doc) => {
    if (!window.confirm(`Send "${doc.title || doc.filename}" as email attachment?`)) return;
    try {
      await sendDocument(doc.id, { recipientEmail: contact.email });
      setMessage({ type: "success", text: `"${doc.title || doc.filename}" sent` });
      const result = await fetchDocuments({ contactEmail: contact.email, limit: 50 });
      setDocs((prev) => {
        const map = new Map();
        prev.forEach((d) => map.set(d.id, d));
        (result.documents || []).forEach((d) => map.set(d.id, d));
        return [...map.values()].sort(
          (a, b) => new Date(b.updatedAt || b.sentAt || 0) - new Date(a.updatedAt || a.sentAt || 0),
        );
      });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  const handleSignFile = async (doc) => {
    if (!window.confirm(`Send "${doc.title || doc.filename}" for e-signature?`)) return;
    try {
      await sendDocumentForSignature(doc.id, { recipientEmail: contact.email });
      setMessage({ type: "success", text: `"${doc.title || doc.filename}" sent for signature` });
      const result = await fetchDocuments({ contactEmail: contact.email, limit: 50 });
      setDocs((prev) => {
        const map = new Map();
        prev.forEach((d) => map.set(d.id, d));
        (result.documents || []).forEach((d) => map.set(d.id, d));
        return [...map.values()].sort(
          (a, b) => new Date(b.updatedAt || b.sentAt || 0) - new Date(a.updatedAt || a.sentAt || 0),
        );
      });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  const docIcon = (doc) => {
    if (doc.type === "nda") return <FileSignature size={14} color="#f59e0b" />;
    if (doc.type === "file") return <File size={14} color="#6366f1" />;
    if (doc.status === "signed") return <CheckCircle size={14} color="#10b981" />;
    return <FileText size={14} color={colors.accent} />;
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize: 11,
          color: colors.textSecondary,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>Documents {!loading && docs.length > 0 && `(${docs.length})`}</span>
        <button
          onClick={handleSendNda}
          disabled={sending || !contact.email}
          title={!contact.email ? "Contact has no email address" : "Send NDA for e-signature"}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            fontSize: 11,
            fontFamily: fonts.body,
            fontWeight: 600,
            color: sending ? colors.textSecondary : colors.accent,
            background: "transparent",
            border: `1px solid ${sending ? colors.border : colors.accent}`,
            borderRadius: 4,
            cursor: sending || !contact.email ? "not-allowed" : "pointer",
            opacity: !contact.email ? 0.5 : 1,
          }}
        >
          {sending ? (
            <Loader2 size={12} className="spin" />
          ) : (
            <FileSignature size={12} />
          )}
          {sending ? "Sending..." : "Send NDA"}
        </button>
      </div>

      {message && (
        <div
          style={{
            padding: "6px 10px",
            fontSize: 12,
            color: message.type === "success" ? "#10b981" : colors.danger,
            background: message.type === "success" ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0" }}>
          <Loader2 size={14} color={colors.textSecondary} className="spin" />
          <span style={{ fontSize: 12, color: colors.textSecondary }}>Loading...</span>
        </div>
      ) : docs.length === 0 ? (
        <div style={{ fontSize: 12, color: colors.textSecondary, padding: "4px 0" }}>
          No documents linked to this contact yet.
        </div>
      ) : (
        <div
          style={{
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {docs.map((doc, i) => {
            const statusCfg =
              DOCUMENT_STATUSES[doc.status] || DOCUMENT_STATUSES.draft;
            const canSend = doc.type === "file" && doc.status !== "sent" && doc.status !== "pending_signature" && doc.status !== "signed";
            const canSign = doc.type === "file" && doc.status !== "pending_signature" && doc.status !== "signed";
            return (
              <div
                key={doc.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  fontSize: 12,
                  borderBottom:
                    i < docs.length - 1
                      ? `1px solid ${colors.border}`
                      : "none",
                }}
              >
                {docIcon(doc)}
                <span
                  style={{
                    flex: 1,
                    color: colors.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {doc.title || doc.filename || "Document"}
                </span>
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: 10,
                    fontSize: 10,
                    fontWeight: 600,
                    color: statusCfg.color,
                    background: statusCfg.bg,
                    whiteSpace: "nowrap",
                  }}
                >
                  {statusCfg.label}
                </span>
                {canSend && (
                  <button
                    onClick={() => handleSendFile(doc)}
                    title="Send as email"
                    style={{ background: "none", border: "none", cursor: "pointer", color: colors.accent, padding: 0, display: "flex" }}
                  >
                    <Send size={13} />
                  </button>
                )}
                {canSign && (
                  <button
                    onClick={() => handleSignFile(doc)}
                    title="Send for e-signature"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#f59e0b", padding: 0, display: "flex" }}
                  >
                    <PenTool size={13} />
                  </button>
                )}
                {doc.signedPdfUrl && (
                  <a
                    href={doc.signedPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View signed PDF"
                    style={{ display: "flex", color: colors.accent }}
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
                {doc.type === "file" && doc.sasUrl && (
                  <a
                    href={doc.sasUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Download"
                    style={{ display: "flex", color: colors.info }}
                  >
                    <Download size={13} />
                  </a>
                )}
                <span
                  style={{
                    fontSize: 11,
                    color: colors.textSecondary,
                    fontFamily: fonts.mono,
                    flexShrink: 0,
                  }}
                >
                  {fmtDate(doc.sentAt || doc.updatedAt)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
